// cspell:ignore mozfullscreenchange msfullscreenchange
import React, { useEffect, useState } from "react";

import { isIOS } from "../utils/device";
import { ExtendedHTMLVideoElement } from "./usePictureInPicture";

type CrossBrowserDocument = {
  mozFullscreenElement: Element;
  msFullscreenElement: Element;
  webkitFullscreenElement: Element;
  mozFullscreen: boolean;
  webkitIsFullscreen: boolean;
  fullScreenMode: boolean;
  mozCancelFullscreen: () => Promise<void>;
  webkitExitFullscreen: () => Promise<void>;
  msExitFullscreen: () => Promise<void>;
} & Document;

type CrossBrowserElement = {
  mozRequestFullscreen: () => Promise<void>;
  webkitRequestFullscreen: () => Promise<void>;
  msRequestFullscreen: () => Promise<void>;
} & HTMLElement;

// iOS Safari uses different fullscreen methods on video elements
type IOSVideoElement = {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
  webkitSupportsFullscreen?: boolean;
} & HTMLVideoElement;

interface UseFullscreen {
  isFullscreenSupported: boolean;
  isFullscreen: boolean;
  requestFullscreen?: () => Promise<void> | undefined;
  exitFullscreen?: () => Promise<void> | undefined;
  toggleFullscreen?: () => Promise<void> | undefined;
}

export const useFullscreen = (
  ref: React.MutableRefObject<HTMLElement | null>,
  videoRef?: React.MutableRefObject<
    HTMLVideoElement | ExtendedHTMLVideoElement | null
  >
): UseFullscreen => {
  const crossBrowserDocument = document as CrossBrowserDocument;
  const initialState = isFullscreenElement(ref);
  const [isFullscreen, setIsFullscreen] = useState(initialState);

  // Check if iOS video fullscreen is available
  const getIOSVideoElement = (): IOSVideoElement | null => {
    if (!isIOS() || !videoRef?.current) return null;
    const video = videoRef.current as IOSVideoElement;
    if (video.webkitSupportsFullscreen && video.webkitEnterFullscreen) {
      return video;
    }
    return null;
  };

  // access various open fullscreen methods
  const requestFullscreen = (): Promise<void> | undefined => {
    // iOS Safari: use webkitEnterFullscreen on video element
    const iosVideo = getIOSVideoElement();
    if (iosVideo) {
      iosVideo.webkitEnterFullscreen?.();
      return Promise.resolve();
    }

    const el = (ref?.current ||
      crossBrowserDocument.documentElement) as CrossBrowserElement;
    if (el.requestFullscreen) return el.requestFullscreen();
    if (el.mozRequestFullscreen) return el.mozRequestFullscreen();
    if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
    if (el.msRequestFullscreen) return el.msRequestFullscreen();
  };

  // access various exit fullscreen methods
  const exitFullscreen = (): Promise<void> | undefined => {
    // iOS Safari: use webkitExitFullscreen on video element
    const iosVideo = getIOSVideoElement();
    if (iosVideo?.webkitDisplayingFullscreen) {
      iosVideo.webkitExitFullscreen?.();
      return Promise.resolve();
    }

    if (crossBrowserDocument.exitFullscreen)
      return crossBrowserDocument.exitFullscreen();
    if (crossBrowserDocument.mozCancelFullscreen)
      return crossBrowserDocument.mozCancelFullscreen();
    if (crossBrowserDocument.webkitExitFullscreen)
      return crossBrowserDocument.webkitExitFullscreen();
    if (crossBrowserDocument.msExitFullscreen)
      return crossBrowserDocument.msExitFullscreen();
  };

  useEffect(() => {
    const handleChange = (): void => {
      setIsFullscreen(isFullscreenElement(ref));
    };

    // iOS video fullscreen change handler
    const handleIOSFullscreenChange = (): void => {
      const iosVideo = videoRef?.current as IOSVideoElement | null;
      if (iosVideo) {
        setIsFullscreen(!!iosVideo.webkitDisplayingFullscreen);
      }
    };

    document.addEventListener("webkitfullscreenchange", handleChange, false);
    document.addEventListener("mozfullscreenchange", handleChange, false);
    document.addEventListener("msfullscreenchange", handleChange, false);
    document.addEventListener("MSFullscreenChange", handleChange, false); // IE11
    document.addEventListener("fullscreenchange", handleChange, false);

    // iOS Safari: listen on video element for fullscreen changes
    const video = videoRef?.current;
    if (video && isIOS()) {
      video.addEventListener(
        "webkitbeginfullscreen",
        handleIOSFullscreenChange,
        false
      );
      video.addEventListener(
        "webkitendfullscreen",
        handleIOSFullscreenChange,
        false
      );
    }

    return () => {
      document.removeEventListener("webkitfullscreenchange", handleChange);
      document.removeEventListener("mozfullscreenchange", handleChange);
      document.removeEventListener("msfullscreenchange", handleChange);
      document.removeEventListener("MSFullscreenChange", handleChange);
      document.removeEventListener("fullscreenchange", handleChange);

      if (video && isIOS()) {
        video.removeEventListener(
          "webkitbeginfullscreen",
          handleIOSFullscreenChange
        );
        video.removeEventListener(
          "webkitendfullscreen",
          handleIOSFullscreenChange
        );
      }
    };
  }, [ref, videoRef]);

  // Check iOS video fullscreen support
  const iosVideoSupported = (): boolean => {
    if (!isIOS() || !videoRef?.current) return false;
    const video = videoRef.current as IOSVideoElement;
    return !!video.webkitSupportsFullscreen;
  };

  const isFullscreenSupported =
    iosVideoSupported() ||
    crossBrowserDocument.fullscreenElement !== undefined ||
    crossBrowserDocument.mozFullscreenElement !== undefined ||
    crossBrowserDocument.webkitFullscreenElement !== undefined ||
    crossBrowserDocument.msFullscreenElement !== undefined;

  return {
    isFullscreenSupported,
    isFullscreen,
    requestFullscreen,
    exitFullscreen,
    toggleFullscreen: isFullscreen ? exitFullscreen : requestFullscreen,
  };
};

const isFullscreenElement = (
  ref: React.MutableRefObject<HTMLElement | null>
): boolean => {
  const crossBrowserDocument = document as CrossBrowserDocument;
  if (ref?.current) {
    return Boolean(
      crossBrowserDocument.fullscreenElement === ref.current ||
        crossBrowserDocument.mozFullscreenElement === ref.current ||
        crossBrowserDocument.webkitFullscreenElement === ref.current ||
        crossBrowserDocument.msFullscreenElement === ref.current
    );
  }

  return Boolean(
    crossBrowserDocument.fullscreenElement ||
      crossBrowserDocument.mozFullscreenElement ||
      crossBrowserDocument.webkitFullscreenElement ||
      crossBrowserDocument.msFullscreenElement ||
      crossBrowserDocument.fullscreen ||
      crossBrowserDocument.mozFullscreen ||
      crossBrowserDocument.webkitIsFullscreen ||
      crossBrowserDocument.fullScreenMode
  );
};
